import React, { useEffect, useMemo, useState } from 'react';
import Principal from '../components/dashboard/principal';
import Conglomerate from '../components/dashboard/conglomerate/Conglomerate';
import MfaConfigurator from '../components/auth/MfaConfigurator';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import ProfileCompletionFlow from '../components/dashboard/ProfileCompletionFlow';
import { useUsuario } from '../context/UsuarioContext';
import {
  mapUsuarioToFormValues,
  createEmptyProfileForm,
  FLOW_TOTAL_STEPS,
} from '../constants/profileForm';

const PersonasDashboard = () => {
  const { usuario, hasLoaded, saveUsuario } = useUsuario();
  const [flowState, setFlowState] = useState({ open: false, mode: 'auto' });
  const [autoPrompted, setAutoPrompted] = useState(false);

  const completedSections = usuario?.seccionesPerfilCompletadas ?? [];
  const completedCount = completedSections.length;
  const totalSections = FLOW_TOTAL_STEPS;
  const hasFullCompletion =
    usuario?.formularioPerfilCompleto === true || completedCount >= totalSections;
  const needsProfileCompletion = !usuario || !hasFullCompletion;
  const hasSeenFlow = usuario?.flujoPerfilMostrado === true;

  useEffect(() => {
    if (!hasLoaded || autoPrompted) {
      return;
    }

    if (needsProfileCompletion && !hasSeenFlow) {
      setFlowState({ open: true, mode: 'auto' });
    }
    setAutoPrompted(true);
  }, [autoPrompted, hasLoaded, hasSeenFlow, needsProfileCompletion]);

  const initialValues = useMemo(
    () => (usuario ? mapUsuarioToFormValues(usuario) : createEmptyProfileForm()),
    [usuario],
  );

  const closeFlow = () => setFlowState((prev) => ({ ...prev, open: false }));

  const handleManualOpen = () => {
    setFlowState({ open: true, mode: 'manual' });
  };

  const handleSaveSection = async (sectionId, { payload }) => {
    try {
      const sectionPayload = payload && Object.keys(payload).length > 0 ? payload : null;
      if (!sectionPayload) {
        throw new Error('Completa al menos un campo de esta etapa antes de continuar.');
      }

      const existingCompleted = new Set(completedSections);
      existingCompleted.add(sectionId);
      const updatedCompleted = Array.from(existingCompleted);

      const dataToSave = {
        ...sectionPayload,
        seccionesPerfilCompletadas: updatedCompleted,
      };

      if (updatedCompleted.length >= totalSections) {
        dataToSave.formularioPerfilCompleto = true;
      }

      const updated = await saveUsuario(dataToSave);
      return updated;
    } catch (error) {
      if (error instanceof Error && error.message) {
        throw error;
      }
      throw new Error('No fue posible guardar esta etapa. Intenta nuevamente.');
    }
  };

  const handleCompleteFlow = async (latestUsuario) => {
    const snapshot = latestUsuario ?? usuario;
    const completed = new Set(snapshot?.seccionesPerfilCompletadas ?? []);
    if (completed.size < totalSections) {
      const remaining = totalSections - completed.size;
      throw new Error(
        `Aún faltan ${remaining} etapa${remaining === 1 ? '' : 's'} por guardar.`,
      );
    }

    await saveUsuario({
      formularioPerfilCompleto: true,
      flujoPerfilMostrado: true,
      seccionesPerfilCompletadas: Array.from(completed),
    });
    closeFlow();
  };

  const handleSkipFlow = async () => {
    try {
      await saveUsuario({ flujoPerfilMostrado: true });
    } catch (error) {
      if (error instanceof Error && error.message) {
        throw error;
      }
      throw new Error('No fue posible registrar tu decisión. Intenta nuevamente.');
    }
    closeFlow();
  };

  const flowOnSkip = flowState.mode === 'auto' ? handleSkipFlow : undefined;
  const flowOnClose = flowState.mode === 'manual' ? closeFlow : undefined;

  const profileProgress = {
    completed: completedCount,
    total: totalSections,
    remaining: Math.max(totalSections - completedCount, 0),
    needsCompletion: needsProfileCompletion,
  };

  return (
    <DashboardLayout>
      <div className="px-[5%] pt-10">
        <div className="text-5xl font-bold text-[#002E6D] pb-5">
          Administra tu dinero
        </div>
        <Principal
          profileProgress={profileProgress}
          onOpenProfileFlow={handleManualOpen}
        />
        <MfaConfigurator variant="personas" />
      </div>
      <Conglomerate />
      <ProfileCompletionFlow
        open={flowState.open}
        initialValues={initialValues}
        completedSections={completedSections}
        onSaveSection={handleSaveSection}
        onComplete={handleCompleteFlow}
        onSkip={flowOnSkip}
        onClose={flowOnClose}
      />
    </DashboardLayout>
  );
};

export default PersonasDashboard;
